import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import line5 from '../../assets/images/svg/line-5.svg';
import vector13 from '../../assets/images/svg/vector-13.svg';
import union1 from '../../assets/images/svg/union-1.svg';
import vector3 from '../../assets/images/svg/vector-3.svg';
import line3 from '../../assets/images/svg/line-5-3.svg';
import rectangle5 from '../../assets/images/png/rectangle-5-4.png';
import metaStep1 from "../../assets/images/svg/meta-step1.svg";
import createSmartLinkButton from '../../assets/images/svg/create-smart-link-button.svg';
import campaignSortIcon from '../../assets/images/svg/campaign-sort-icon.svg';
import brandLogo from "../../assets/images/svg/white-logo-new.svg";
import { ROUTES } from "../../components/consts/routes.consts";
import CampaignCard from "../../components/CampaignCard";
import SortSelector, { SORT_OPTIONS } from "../../components/SortSelector";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import EditConfirmationModal from "../../components/EditConfirmationModal";
import { useGetMetaAdsCampaignsFromMetaQuery, useUpdateMetaAdsCampaignStatusMutation, useDuplicateMetaAdsCampaignMutation, useGetMetaAdsAuthStatusQuery, useGetDraftCampaignsQuery, useDeleteMetaAdsCampaignMutation, useDeleteDraftCampaignMutation, MetaCampaignFromMeta, DraftCampaign } from "../../components/apis/metaAdsApi";
import { CampaignCardData, formatMoney } from "../../utils/campaignTransform.util";

// Helper function to transform Meta campaign to CampaignCardData
const transformMetaCampaignFromMeta = (metaCampaign: MetaCampaignFromMeta): CampaignCardData => {
  // Format status
  const status =
    metaCampaign.status === "ACTIVE"
      ? ("Active" as const)
      : metaCampaign.status === "PAUSED"
        ? ("Paused" as const)
        : ("Active" as const);

  // Format dates
  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "None";
    return dayjs(dateStr).format("DD MMM YYYY");
  };

  // Calculate budget string
  const getBudgetString = () => {
    // Convert string/number to number and handle both types
    const dailyBudget = metaCampaign.daily_budget
      ? (typeof metaCampaign.daily_budget === 'string' ? parseFloat(metaCampaign.daily_budget) : metaCampaign.daily_budget) / 100
      : undefined;
    const lifetimeBudget = metaCampaign.lifetime_budget
      ? (typeof metaCampaign.lifetime_budget === 'string' ? parseFloat(metaCampaign.lifetime_budget) : metaCampaign.lifetime_budget) / 100
      : undefined;

    // Check if campaign has an end date
    const hasEndDate = metaCampaign.stop_time && metaCampaign.stop_time !== "";

    // Use daily_budget if available, otherwise use lifetime_budget
    const dailyAmount = dailyBudget || (lifetimeBudget ? lifetimeBudget / 1 : undefined);

    if (dailyAmount) {
      if (hasEndDate) {
        // Calculate total budget: daily × days
        const startDate = dayjs(metaCampaign.start_time);
        const endDate = dayjs(metaCampaign.stop_time);
        // Calculate days difference using ceil to include partial days
        const days = Math.ceil(endDate.diff(startDate, 'hour') / 24);

        if (days > 0) {
          const total = dailyAmount * days;
          return `${formatMoney(total, metaCampaign.currency)} (${formatMoney(dailyAmount, metaCampaign.currency)}/day)`;
        }
      }
      // No end date - show daily budget only
      return `${formatMoney(dailyAmount, metaCampaign.currency)}/day`;
    }

    return "Not set";
  };

  // Format numbers with commas
  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  // Format last updated - shows Today/Yesterday with exact time from API (no local conversion)
  const formatLastUpdatedExact = (dateStr?: string | null) => {
    if (!dateStr) return "N/A";

    // Parse the API date string and extract time directly from it
    // Format: "2026-01-17T20:08:11+0200" or similar ISO formats
    const apiDate = new Date(dateStr);

    // Extract time directly from the parsed date (hours and minutes)
    const hours = apiDate.getHours().toString().padStart(2, '0');
    const minutes = apiDate.getMinutes().toString().padStart(2, '0');
    const timeStr = `${hours}:${minutes}`;

    // Get date in UTC to avoid local timezone issues
    const apiDateUtc = Date.UTC(
      apiDate.getUTCFullYear(),
      apiDate.getUTCMonth(),
      apiDate.getUTCDate()
    );

    // Get today's date in UTC
    const today = new Date();
    const todayUtc = Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate()
    );

    // Calculate difference in days
    const diffTime = todayUtc - apiDateUtc;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today, ${timeStr}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${timeStr}`;
    } else {
      // Show date with time from API
      const day = apiDate.getUTCDate().toString().padStart(2, '0');
      const month = (apiDate.getUTCMonth() + 1).toString().padStart(2, '0');
      const year = apiDate.getUTCFullYear();
      return `${day}/${month}/${year}, ${timeStr}`;
    }
  };

  return {
    id: metaCampaign.id,
    name: metaCampaign.name,
    status,
    conversionPage: "Meta Ads Campaign",
    startDate: formatDate(metaCampaign.start_time),
    endDate: formatDate(metaCampaign.stop_time),
    budget: getBudgetString(),
    currency: metaCampaign.currency,
    engagement: "N/A", // Not available from Meta without insights call
    visits: "N/A",
    clicks: "N/A",
    cost: "N/A",
    results: {
      clicks: "N/A",
      ctr: "N/A",
      conversions: "N/A",
    },
    lastUpdated: formatLastUpdatedExact(metaCampaign.updated_time),
    isFromMeta: true,
    objective: metaCampaign.objective,
    accountId: metaCampaign.account_id,
    createdAt: metaCampaign.created_time,
    updatedAt: metaCampaign.updated_time,
    linkUrls: metaCampaign.linkUrls || [], // Add linkUrls from Meta API response
    videos: metaCampaign.videos || [], // Add videos from Meta API response
  };
};

// Helper function to transform Draft campaign to CampaignCardData
const transformDraftCampaign = (
  draft: DraftCampaign,
  currency?: string,
): CampaignCardData => {
  // Draft campaigns always show as "Draft" status
  const status = "Draft" as const;

  // Format dates
  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "None";
    return dayjs(dateStr).format("DD MMM YYYY");
  };

  // Calculate budget string
  const getBudgetString = () => {
    const budget = draft.budget?.amount || 0;
    const budgetType = draft.budget?.type || "DAILY";
    const endDate = draft.schedule?.endDate;
    const startDate = draft.schedule?.startDate;

    if (!budget) return "Not set";

    // Check if campaign has an end date
    const hasEndDate = endDate && endDate !== "None" && endDate.trim() !== "";

    if (hasEndDate && startDate) {
      // Calculate total budget: daily × days
      const start = dayjs(startDate);
      const end = dayjs(endDate);
      const days = end.diff(start, 'day') + 1; // Include both start and end days

      if (days > 0) {
        const total = budget * days;
        return `${formatMoney(total, currency)} (${formatMoney(budget, currency)}/day)`;
      }
    }

    // No end date or invalid dates - show daily budget only
    return `${formatMoney(budget, currency)}/day`;
  };

  // Format last updated - shows Today/Yesterday with exact time from API (no local conversion)
  const formatLastUpdatedExact = (dateStr?: string) => {
    if (!dateStr) return "N/A";

    // Parse the API date string and extract time directly from it
    const apiDate = new Date(dateStr);

    // Extract time directly from the parsed date (hours and minutes)
    const hours = apiDate.getHours().toString().padStart(2, '0');
    const minutes = apiDate.getMinutes().toString().padStart(2, '0');
    const timeStr = `${hours}:${minutes}`;

    // Get date in UTC to avoid local timezone issues
    const apiDateUtc = Date.UTC(
      apiDate.getUTCFullYear(),
      apiDate.getUTCMonth(),
      apiDate.getUTCDate()
    );

    // Get today's date in UTC
    const today = new Date();
    const todayUtc = Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate()
    );

    // Calculate difference in days
    const diffTime = todayUtc - apiDateUtc;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today, ${timeStr}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${timeStr}`;
    } else {
      // Show date with time from API
      const day = apiDate.getUTCDate().toString().padStart(2, '0');
      const month = (apiDate.getUTCMonth() + 1).toString().padStart(2, '0');
      const year = apiDate.getUTCFullYear();
      return `${day}/${month}/${year}, ${timeStr}`;
    }
  };

  return {
    id: draft._id,
    name: draft.campaignName,
    status,
    conversionPage: "Draft Campaign",
    startDate: formatDate(draft.schedule?.startDate),
    endDate: formatDate(draft.schedule?.endDate),
    budget: getBudgetString(),
    currency,
    engagement: "N/A",
    visits: "N/A",
    clicks: "N/A",
    cost: "N/A",
    results: {
      clicks: "N/A",
      ctr: "N/A",
      conversions: "N/A",
    },
    lastUpdated: formatLastUpdatedExact(draft.updatedAt),
    isFromMeta: false,
    isDraft: true,
    objective: draft.objective,
    accountId: draft.adAccountId,
    createdAt: draft.createdAt,
    updatedAt: draft.updatedAt,
    linkUrls: draft.metadata?.spotifyUrl ? [draft.metadata.spotifyUrl] : [],
    videos: draft.s3Videos?.map((video: any) => ({
      id: video.key,
      title: video.fileName || `Video ${video.key?.substring(0, 8)}...`,
    })) || [],
  };
};

const CampaignNavbar: React.FC = () => {
  return (
    <div className="w-full py-8 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 mb-10 fixed top-0 left-0 right-0 z-[100] bg-[#000000] h-[86px]">
      <div className="flex items-center justify-between gap-10">
        <div className="flex items-center gap-10 -mt-2">
          <img src={brandLogo} alt="PitchPlaylists" className="h-9 w-auto" />
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-white/10"></div>
    </div>
  );
};

export default function MyMetaCampaign() {
  const navigate = useNavigate();
  const [tracksTabState, setTracksTabState] = useState("tracks");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState(SORT_OPTIONS[0]);
  const [hasShownError, setHasShownError] = useState(false);
  const [updatingCampaignId, setUpdatingCampaignId] = useState<string | null>(null);
  const [duplicatingCampaignId, setDuplicatingCampaignId] = useState<string | null>(null);
  const [deletingCampaignId, setDeletingCampaignId] = useState<string | null>(null);

  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    campaignId: string | number;
    campaignName: string;
    isDraft: boolean;
  }>({
    isOpen: false,
    campaignId: "",
    campaignName: "",
    isDraft: false,
  });

  // Edit confirmation modal state
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    campaignId: string | number;
  }>({
    isOpen: false,
    campaignId: "",
  });

  // Check Facebook connection status
  const { data: authStatusData, isLoading: isAuthStatusLoading } = useGetMetaAdsAuthStatusQuery();
  const isFacebookConnected = authStatusData?.connected ?? false;

  // Get adAccountId from session storage
  const [adAccountId, setAdAccountId] = useState<string | null>(null);
  useEffect(() => {
    try {
      const metaAdsSelections = sessionStorage.getItem('metaAdsSelections');
      if (metaAdsSelections) {
        const parsed = JSON.parse(metaAdsSelections);
        if (parsed?.adAccount?.id) {
          setAdAccountId(parsed.adAccount.id);
        }
      }
    } catch (e) {
      console.error('Failed to parse metaAdsSelections:', e);
    }
  }, []);

  // Fetch campaigns directly from Meta - ONLY for selected ad account
  const {
    data: metaCampaignsData,
    isLoading: isMetaCampaignsLoading,
    error: metaCampaignsError,
    refetch: refetchMetaCampaigns,
  } = useGetMetaAdsCampaignsFromMetaQuery(
    { limit: 100, adAccountId: adAccountId || undefined },
    {
      skip: !isFacebookConnected || !adAccountId, // Skip if not connected OR no adAccountId selected
      pollingInterval: 60000, // Refetch every minute
    }
  );

  // Fetch draft campaigns
  const {
    data: draftsData,
    isLoading: isDraftsLoading,
    error: draftsError,
    refetch: refetchDrafts,
  } = useGetDraftCampaignsQuery(
    { adAccountId: adAccountId || undefined },
    {
      skip: !adAccountId, // Skip if no adAccountId selected
      pollingInterval: 60000, // Refetch every minute
    }
  );

  // Status update mutation
  const [updateCampaignStatus, { isLoading: isUpdatingStatus }] = useUpdateMetaAdsCampaignStatusMutation();

  // Duplicate campaign mutation
  const [duplicateCampaign, { isLoading: isDuplicating }] = useDuplicateMetaAdsCampaignMutation();

  // Delete mutations
  const [deleteCampaign] = useDeleteMetaAdsCampaignMutation();
  const [deleteDraft] = useDeleteDraftCampaignMutation();

  // Redirect to Meta Ads Builder if not connected
  useEffect(() => {
    if (!isAuthStatusLoading && !isFacebookConnected && !hasShownError) {
      setHasShownError(true);
      toast.error("Please connect your Facebook account first");
      navigate(ROUTES.META_ADS_BUILDER);
    }
  }, [isFacebookConnected, isAuthStatusLoading, navigate, hasShownError]);

  // Hide header
  useEffect(() => {
    const header = document.querySelector("header") as HTMLElement | null;
    if (header) {
      const prevDisplay = header.style.display;
      header.dataset.prevDisplay = prevDisplay;
      header.style.display = "none";
      return () => {
        header.style.display = header.dataset.prevDisplay || "";
        delete header.dataset.prevDisplay;
      };
    }
  }, []);

  // Transform Meta campaigns to frontend format
  const metaCampaigns: CampaignCardData[] = metaCampaignsData?.campaigns
    ? metaCampaignsData.campaigns.map(transformMetaCampaignFromMeta)
    : [];

  // All campaigns in an ad account share its currency; use it for drafts too.
  const accountCurrency = metaCampaignsData?.campaigns?.[0]?.currency;

  // Transform Draft campaigns to frontend format
  const draftCampaigns: CampaignCardData[] = draftsData?.drafts
    ? draftsData.drafts.map((draft) =>
        transformDraftCampaign(draft, accountCurrency),
      )
    : [];

  // Combine both campaigns (drafts first, then Meta campaigns)
  const campaigns: CampaignCardData[] = [...draftCampaigns, ...metaCampaigns];

  // Helper function to extract numeric value from engagement string
  const extractEngagementValue = (engagement: string): number => {
    if (!engagement || engagement === 'N/A') return 0;

    // Remove currency symbols and extract numeric value
    const numericValue = engagement.replace(/[^\d.]/g, '');
    return parseFloat(numericValue) || 0;
  };

  // Helper function to extract numeric value from cost string
  const extractCostValue = (cost: string): number => {
    if (!cost || cost === 'N/A') return 0;

    // Remove currency symbols and extract numeric value
    const numericValue = cost.replace(/[^\d.]/g, '');
    return parseFloat(numericValue) || 0;
  };

  // Helper function to get date for sorting
  const getSortDate = (campaign: CampaignCardData): Date => {
    // Try to use createdAt or updatedAt from raw data
    if (campaign.createdAt) {
      return new Date(campaign.createdAt);
    }
    if (campaign.startDate && campaign.startDate !== 'N/A') {
      return new Date(campaign.startDate);
    }
    if (campaign.lastUpdated && campaign.lastUpdated !== 'N/A') {
      return new Date(campaign.lastUpdated);
    }
    // Default to old date if no date found
    return new Date(0);
  };

  // Sort campaigns based on selected sort option
  const sortedCampaigns = useMemo(() => {
    const sorted = [...campaigns];

    switch (sortOption) {
      case "Start Date: New to Old":
        return sorted.sort((a, b) => {
          const dateA = getSortDate(a);
          const dateB = getSortDate(b);
          return dateB.getTime() - dateA.getTime();
        });

      case "Start Date: Old to New":
        return sorted.sort((a, b) => {
          const dateA = getSortDate(a);
          const dateB = getSortDate(b);
          return dateA.getTime() - dateB.getTime();
        });

      case "Most Engaged [All Time]":
        return sorted.sort((a, b) => {
          const engagementA = extractEngagementValue(a.engagement);
          const engagementB = extractEngagementValue(b.engagement);
          return engagementB - engagementA;
        });

      case "Lowest cost/Results [All Time]":
        return sorted.sort((a, b) => {
          const costA = extractCostValue(a.cost);
          const costB = extractCostValue(b.cost);
          return costA - costB;
        });

      default:
        return sorted;
    }
  }, [campaigns, sortOption]);

  const handleDuplicate = async (campaignId: string | number) => {
    try {
      setDuplicatingCampaignId(campaignId.toString());
      const result = await duplicateCampaign({ campaignId: campaignId.toString() }).unwrap();

      toast.success(`Campaign duplicated successfully! New campaign: ${result.newCampaignName}`);

      // Refetch campaigns to show the duplicated one
      refetchMetaCampaigns();
    } catch (error: any) {
      console.error("Error duplicating campaign:", error);
      toast.error(error?.data?.msg || error?.data?.error || "Failed to duplicate campaign. Please try again.");
    } finally {
      setDuplicatingCampaignId(null);
    }
  };

  const handlePause = async (campaignId: string | number) => {
    try {
      setUpdatingCampaignId(campaignId.toString());

      // Find the current campaign to determine its status
      const campaign = campaigns.find(c => c.id === campaignId);
      if (!campaign) {
        toast.error("Campaign not found");
        return;
      }

      // Determine new status (toggle between ACTIVE and PAUSED)
      const newStatus = campaign.status === "Active" ? "PAUSED" : "ACTIVE";

      // Call the API to update status on Facebook
      await updateCampaignStatus({
        campaignId: campaignId.toString(),
        status: newStatus,
      }).unwrap();

      // Show success message
      const action = newStatus === "ACTIVE" ? "activated" : "paused";
      toast.success(`Campaign ${action} successfully!`);

      // Refetch campaigns to get updated status from Meta
      refetchMetaCampaigns();
    } catch (error: any) {
      console.error("Error updating campaign status:", error);
      toast.error(error?.data?.msg || "Failed to update campaign status. Please try again.");
    } finally {
      setUpdatingCampaignId(null);
    }
  };

  const handleDelete = async (campaignId: string | number, isDraft: boolean) => {
    // Find campaign name for the modal
    const campaign = campaigns.find(c => c.id === campaignId);
    if (!campaign) {
      toast.error("Campaign not found");
      return;
    }

    // Show confirmation modal
    setDeleteModal({
      isOpen: true,
      campaignId,
      campaignName: campaign.name,
      isDraft,
    });
  };

  const handleDeleteConfirm = async () => {
    const { campaignId, isDraft } = deleteModal;

    try {
      setDeletingCampaignId(campaignId.toString());

      if (isDraft) {
        // Delete draft campaign
        await deleteDraft(campaignId.toString()).unwrap();
        toast.success("Draft deleted successfully!");
        refetchDrafts();
      } else {
        // Delete campaign from Meta
        await deleteCampaign(campaignId.toString()).unwrap();
        toast.success("Campaign deleted successfully from Meta!");
        refetchMetaCampaigns();
      }

      // Close modal after successful deletion
      setDeleteModal({
        isOpen: false,
        campaignId: "",
        campaignName: "",
        isDraft: false,
      });
    } catch (error: any) {
      console.error("Error deleting:", error);
      toast.error(error?.data?.msg || error?.data?.error || `Failed to delete ${isDraft ? "draft" : "campaign"}. Please try again.`);
    } finally {
      setDeletingCampaignId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({
      isOpen: false,
      campaignId: "",
      campaignName: "",
      isDraft: false,
    });
  };

  const handleEdit = async (campaignId: string | number) => {
    // Show confirmation modal
    setEditModal({
      isOpen: true,
      campaignId,
    });
  };

  const handleEditConfirm = () => {
    const { campaignId } = editModal;

    // Close modal and navigate to edit page
    setEditModal({
      isOpen: false,
      campaignId: "",
    });

    // Navigate to edit page with campaignId
    navigate(`/meta-new-campaign?campaignId=${campaignId}`);
  };

  const handleEditCancel = () => {
    setEditModal({
      isOpen: false,
      campaignId: "",
    });
  };

  // Show loading while checking auth status
  if (isAuthStatusLoading) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center bg-black">
        <CircularProgress sx={{ color: "#58F2A9" }} />
      </div>
    );
  }

  // Show loading state
  if (isMetaCampaignsLoading || isDraftsLoading) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center bg-black">
        <CircularProgress sx={{ color: "#58F2A9" }} />
      </div>
    );
  }

  // Show error state
  if (metaCampaignsError) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <p className="text-red-400 mb-4">Error loading campaigns from Meta. Please try again.</p>
          <button
            onClick={() => refetchMetaCampaigns()}
            className="px-6 py-2 bg-[#58F2A9] rounded-full text-black font-semibold"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen pt-28 pb-10 flex flex-col items-center">
      <CampaignNavbar />
      <div className="w-full max-w-[1409px] px-4 md:px-8 py-6 flex justify-center">
        <div className="flex flex-col items-center gap-6 w-full">

          <div className="flex flex-col w-full items-start gap-16">
            <div className="flex flex-col items-start gap-[15px] relative self-stretch w-full flex-[0_0_auto]">
              <div className="flex items-end gap-2.5 relative self-stretch w-full flex-[0_0_auto]">
                <div className="flex flex-col w-full lg:w-3/4 items-start gap-[30px] relative">
                  <h1 className="font-h-1 font-[number:var(--h-1-font-weight)] text-white text-[length:var(--h-1-font-size)] tracking-[var(--h-1-letter-spacing)] leading-[var(--h-1-line-height)] [font-style:var(--h-1-font-style)]">
                    Meta Ads Builder
                  </h1>
                </div>
              </div>

              {/* NEW CAMPAIGN | MY CAMPAIGNS TABS */}
              <nav className="flex items-center gap-6 pt-4 pb-6 px-0 relative self-stretch w-full flex-[0_0_auto]">
                <div className="flex h-[30px] w-[141px] items-center gap-1 relative">
                  <button
                    className="relative w-[143px] mr-[-2.00px]  font-medium text-[#4b4f55] text-lg tracking-[0] leading-[23.4px] hover:text-white transition-colors cursor-pointer text-left"
                    onClick={() => navigate(ROUTES.META_ADS_BUILDER)}
                  >
                    New Campaign
                  </button>
                </div>

                <img
                  className="relative w-0.5 h-[30px] object-cover"
                  alt=""
                  src={vector13}
                />

                <div className="flex w-[177px] items-center gap-[5px] relative mr-[-37.00px]">
                  <span className="relative w-[141px] font-medium text-white text-lg tracking-[0] leading-[23.4px]">
                    My Campaigns
                  </span>
                </div>
              </nav>

              <div className="flex flex-col items-center p-8 lg:p-16 relative self-stretch w-full flex-[0_0_auto] bg-[#0e0e10] rounded-[40px] overflow-hidden border-[none] shadow-[0px_10px_40px_#060c1180] before:content-[''] before:absolute before:inset-0 before:p-0.5 before:rounded-[40px] before:[background:linear-gradient(0deg,rgba(255,255,255,1)_0%,rgba(0,26,255,1)_45%,rgba(27,118,255,1)_100%)] before:[-webkit-mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)] before:[-webkit-mask-composite:xor] before:[mask-composite:exclude] before:z-[1] before:pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-full rounded-[40px] overflow-hidden">
                  <div className="relative top-[-100px] lg:top-[-285px] left-1/2 transform -translate-x-1/2 w-[90vw] lg:w-[1409px] h-[200px] lg:h-[452px] rounded-full blur-[110px] bg-[linear-gradient(270deg,rgba(182,211,255,1)_0%,rgba(0,34,255,1)_53%,rgba(34,255,244,1)_100%)]" />
                </div>

                <div className="flex flex-col w-full items-start justify-center gap-8 relative flex-[0_0_auto]">
                  <div className="flex flex-col items-start gap-3 w-full">
                    <div className="flex w-full items-center justify-between">

                      {/* CREATE NEW SMART LINK BUTTON */}
                      {/* <button
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full hover:translate-y-[-1px] transition-transform ml-auto"
                        style={{
                          background: 'linear-gradient(to right, #58F2A9, #1C3941)',
                          border: '1px solid #58F2A9'
                        }}
                      >
                        <span className="font-semibold text-white text-base leading-[100%] tracking-[0] [font-family:'Poppins',Helvetica]">
                          Create new smart link
                        </span>
                      </button> */}

                    </div>

                    {/* DESCRIPTION */}
                    <div className="flex flex-col items-start gap-3 pr-0 w-full">
                      <div className="flex items-start gap-3 w-full">
                        <p className="flex-1 text-white text-[40px] font-[600] leading-[100%] tracking-[0]  font-[style:'SemiBold']">
                          Your {campaigns.length > 1 ? 'campaigns' : 'campaign'} {campaigns.length > 1 ? 'are' : 'is'} listed here
                        </p>
                      </div>
                      <div className="flex items-start gap-3 w-full">
                        <p className="flex-1 text-white text-[22px] font-[500] leading-[26px] tracking-[0] font-[style:'Medium']">
                          See how your tracks are performing across {campaigns.length > 1 ? 'campaigns' : 'campaign'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <img
                    className="relative w-full h-px object-cover"
                    alt=""
                    src={line3}
                  />

                  <section className="flex flex-col items-start gap-2.5 relative self-stretch w-full flex-[0_0_auto]">
                    <div className="inline-flex items-center gap-6 relative flex-[0_0_auto] w-full justify-between">

                      <div className="flex flex-col w-full lg:w-11/12 items-start gap-6 relative">
                        <h2 className="mt-[-1.00px] opacity-[0.88] text-white text-[32px] font-[600]  leading-[100%] tracking-[0] whitespace-nowrap relative w-fit">
                          My {campaigns.length > 1 ? 'Campaigns' : 'Campaign'} 
                        </h2>
                      </div>

                      {/* TRACKS/PLAYLISTS TABS */}
                      {/* <div className="inline-flex flex-col items-start gap-2 p-[6.4px] relative flex-[0_0_auto] bg-[#878da11a] rounded-[99px]">
                        <div className="inline-flex items-center justify-center gap-5 relative flex-[0_0_auto]">
                          <button
                            className={`inline-flex items-center justify-center gap-2 px-[25.6px] py-[12.8px] relative flex-[0_0_auto] rounded-[99px] ${tracksTabState === "tracks" ? "bg-white" : ""
                              }`}
                            onClick={() => setTracksTabState("tracks")}
                          >
                            <span className={`relative flex items-center justify-center w-fit mt-[-0.80px] font-medium text-base tracking-[0] leading-[normal] ${tracksTabState === "tracks" ? "text-black" : "text-white"
                              }`}>
                              Tracks
                            </span>
                          </button>

                          <button
                            className={`inline-flex items-center justify-center gap-2 px-[25.6px] py-[12.8px] relative flex-[0_0_auto] rounded-[99px] ${tracksTabState === "playlists" ? "bg-white" : ""
                              }`}
                            onClick={() => setTracksTabState("playlists")}
                          >
                            <span className={`relative flex items-center justify-center w-fit mt-[-0.80px] font-medium text-base tracking-[0] leading-[normal] ${tracksTabState === "playlists" ? "text-black" : "text-white"
                              }`}>
                              Playlists
                            </span>
                          </button>
                        </div>
                      </div> */}

                    </div>

                    {/* SEARCH AND SORT BAR */}
                    <div className="flex items-center justify-between w-full mt-6">
                      {/* SEARCH BAR - LEFT SIDE */}
                      <div className="flex-1 relative mb-[20px]">
                        <svg
                          className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#878da1]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                        <input
                          type="text"
                          placeholder="Search campaigns..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-[339px] h-[48px] pl-12 pr-4 rounded-[10px] bg-[#1A1D24] text-white text-base  placeholder:text-[#878da1] focus:outline-none focus:ring-2 focus:ring-[#58F2A9]"
                        />
                      </div>

                      {/* SORT SELECTOR - RIGHT SIDE */}
                      <SortSelector
                        selectedSort={sortOption}
                        onSortChange={setSortOption}
                      />
                    </div>

                    {/* CAMPAIGNS LIST */}
                    <div className="flex flex-col gap-6 relative self-stretch w-full">
                      {/* Filter and sort campaigns based on search query */}
                      {(() => {
                        const filteredCampaigns = sortedCampaigns.filter(campaign =>
                          campaign.name.toLowerCase().includes(searchQuery.toLowerCase())
                        );

                        // Show "No results found" when no campaigns match search
                        if (filteredCampaigns.length === 0) {
                          return (
                            <div className="text-center text-[#878da1] text-lg py-12">
                              {searchQuery ? "No results found" : "No campaigns found from Meta"}
                            </div>
                          );
                        }

                        // Show filtered and sorted campaigns
                        return filteredCampaigns.map((campaign, index) => (
                          <CampaignCard
                            key={campaign.id}
                            campaign={campaign}
                            index={index}
                            onDuplicate={handleDuplicate}
                            onPause={handlePause}
                            onDelete={handleDelete}
                            onEdit={handleEdit}
                            isUpdating={updatingCampaignId === campaign.id}
                            isDuplicating={duplicatingCampaignId === campaign.id}
                            isDeleting={deletingCampaignId === campaign.id}
                          />
                        ));
                      })()}
                    </div>

                  </section>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        campaignName={deleteModal.campaignName}
        isDraft={deleteModal.isDraft}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isDeleting={deletingCampaignId === deleteModal.campaignId.toString()}
      />

      {/* Edit Confirmation Modal */}
      <EditConfirmationModal
        isOpen={editModal.isOpen}
        onConfirm={handleEditConfirm}
        onCancel={handleEditCancel}
      />
    </div>
  );
}
